import React, { useState, useEffect } from "react";

const APPLICATION_TYPE_COLORS = {
    NDA: "ev-badge-blue",
    ANDA: "ev-badge-orange",
    BLA: "ev-badge-purple",
};

const MARKETING_STATUS_BADGE = {
    "Prescription": "discovery-badge-orange",
    "Over-the-counter": "discovery-badge-orange",
    "Discontinued": "discovery-badge-orange",
    "None (Tentative Approval)": "discovery-badge-orange",
};

const ApplicationRow = ({ app, isActive, isChecked, onSelect, onCheck }) => {
    const product = app.products?.[0];
    const docCount = (app.documents || []).filter((d) => d.document_url).length;
    const marketingStatus = product?.marketing_status || null;
    const statusBadgeClass = MARKETING_STATUS_BADGE[marketingStatus] || "discovery-badge-neutral";

    return (
        <tr
            className={`cursor-pointer ${isActive ? "discovery-row-active" : ""}`}
            onClick={() => onSelect?.(app)}
        >
            <td className="align-top w-55" style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
                <div className="d-flex align-items-start gap-2">
                    <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                            e.stopPropagation();
                            onCheck?.(app.id, e.target.checked);
                        }}
                    />
                    <div style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                            <span className={`ev-badge-pill ev-fs-11 ${APPLICATION_TYPE_COLORS[app.application_type] || "ev-badge-blue"}`}>
                                {app.application_type || "—"}
                            </span>
                            <p className="ev-reg-p-dark ev-bold-med mb-0" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                                {(app.brand_name || []).join(", ") || "—"}
                            </p>
                        </div>
                        <p className="ev-small-p-dark mb-0" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                            {app.application_number || "—"}
                            {product?.dosage_form ? ` · ${product.dosage_form}` : ""}
                        </p>
                        <p className="ev-small-p-dark mb-0" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                            {app.sponsor_name || "—"}
                        </p>
                    </div>
                </div>
            </td>

            <td className="align-top w-35 text-end">
                <div className="d-flex flex-column align-items-end gap-1">
                    {docCount > 0 ? (
                        <span className="discovery-badge-green">
                            <i className="bi bi-file-earmark-pdf me-1"></i>
                            {docCount} document{docCount > 1 ? "s" : ""}
                        </span>
                    ) : (
                        <span className="discovery-badge-unknown" style={{ width: "auto", border: "1px solid var(--color8)" }}>
                            No documents
                        </span>
                    )}
                    {marketingStatus && (
                        <span className={statusBadgeClass} style={{ width: "auto" }}>
                            {marketingStatus}
                        </span>
                    )}
                </div>
            </td>
        </tr>
    );
};

const GroupCard = ({ group, selectedResult, onSelectResult, selectedIds, onSelectOne, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    const groupIds = (group.applications || []).map((a) => a.id);
    const allChecked = groupIds.length > 0 && groupIds.every((id) => selectedIds.includes(id));
    const someChecked = groupIds.some((id) => selectedIds.includes(id));

    const handleGroupCheck = (e) => {
        e.stopPropagation();
        groupIds.forEach((id) => onSelectOne?.(id, !allChecked));
    };

    return (
        <>
            <tr
                className="cursor-pointer"
                onClick={() => setIsOpen((o) => !o)}
            >
                <td className="align-top w-55" style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
                    <div className="d-flex align-items-start gap-2">
                        <input
                            type="checkbox"
                            checked={allChecked}
                            ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                            onChange={handleGroupCheck}
                            onClick={(e) => e.stopPropagation()}
                        />
                        <p className="ev-reg-p-dark ev-bold-med mb-0" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                            {group.groupLabel}
                        </p>
                    </div>
                </td>

                <td className="align-top w-35 text-end">
                    <div className="d-flex align-items-center justify-content-end gap-2">
                        <span className="ev-badge-pill ev-fs-11 ev-badge-light">
                            {group.applications.length}
                        </span>
                        <i className={`bi ${isOpen ? "bi-chevron-up" : "bi-chevron-down"} ev-fs-11`} />
                    </div>
                </td>
            </tr>

            {isOpen && group.applications.map((app) => (
                <ApplicationRow
                    key={app.id}
                    app={app}
                    isActive={selectedResult?.id === app.id}
                    isChecked={selectedIds.includes(app.id)}
                    onSelect={onSelectResult}
                    onCheck={onSelectOne}
                />
            ))}
        </>
    );
};

const FDAResultsList = ({
    results = [],
    selectedResult,
    onSelectResult,
    selectedIds = [],
    selectAll = false,
    onSelectAll,
    onSelectOne,
}) => {
    const groups = results;
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);
    const selectedCount = selectedIds.length;

    const visibleGroups = showSelectedOnly
        ? groups
            .map((g) => ({
                ...g,
                applications: g.applications.filter((a) => selectedIds.includes(a.id)),
            }))
            .filter((g) => g.applications.length > 0)
        : groups;

    useEffect(() => {
        if (showSelectedOnly && selectedCount === 0) {
            setShowSelectedOnly(false);
        }
    }, [showSelectedOnly, selectedCount]);

    if (results.length === 0) {
        return (
            <div className="ev-card-2 p-4 vh-55 d-flex flex-column align-items-center justify-content-center text-center">
                <i className="bi bi-journal-x ev-fs-50 text-muted mb-3"></i>
                <p className="ev-reg-p-dark ev-bold-med mb-1">No FDA Applications Found</p>
                <p className="ev-small-p-blue mb-0">Run a search to fetch results.</p>
            </div>
        );
    }

    return (
        <div className="table-container" style={{ overflowX: "hidden" }}>
            <table className="table-custom1 mt-0 overflow-visible w-100 ev-message-card3 ev-radius-xsmall py-0 px-2">
                <thead>
                    <tr>
                        <th className="w-60 p-0">
                            <div className="d-flex align-items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={selectAll}
                                    onChange={(e) => onSelectAll?.(e.target.checked)}
                                />
                                <label className="mb-0">Applications ({results.reduce((sum, g) => sum + (g.applications?.length || 0), 0)})</label>
                                <span className="ev-btn-white-fill-round ev-btn-xsmall ev-small-p-blue px-3 pe-1 ms-1 ev-bold-med">
                                    {selectedCount} of {results.reduce((sum, g) => sum + (g.applications?.length || 0), 0)} Selected
                                    <button
                                        type="button"
                                        className={`ev-btn-xsmall ev-bold-med ev-radius-xlarge ${
                                            showSelectedOnly
                                                ? "ev-btn-blue-fill-round ms-2"
                                                : "ev-btn-skyblue-fill-round ev-small-p-blue ms-2"
                                        }`}
                                        onClick={() => setShowSelectedOnly((prev) => !prev)}
                                        disabled={!showSelectedOnly && selectedCount === 0}
                                    >
                                        {showSelectedOnly ? "Show All" : "Show Selected"}
                                    </button>
                                </span>
                            </div>
                        </th>
                    </tr>
                </thead>
            </table>

            <div className="scrollable-element vh-50 pe-1" style={{ overflowX: "hidden" }}>
                <table className="table-custom1 overflow-hidden w-100" style={{ tableLayout: "fixed" }}>
                    <colgroup>
                        <col style={{ width: "65%" }} />
                        <col style={{ width: "35%" }} />
                    </colgroup>
                    <tbody>
                        {visibleGroups.length === 0 ? (
                            <tr>
                                <td colSpan="2" className="text-center py-5">
                                    <i className="bi bi-funnel ev-fs-50 text-muted mb-3 d-block"></i>
                                    <p className="ev-reg-p-dark ev-bold-med mb-1">No Selected Applications</p>
                                    <p className="ev-small-p-dark text-muted mb-0">
                                        Select applications from the list to view them here.
                                    </p>
                                </td>
                            </tr>
                        ) : (
                            visibleGroups.map((group, idx) => (
                                <GroupCard
                                    key={group.groupKey}
                                    group={group}
                                    selectedResult={selectedResult}
                                    onSelectResult={onSelectResult}
                                    selectedIds={selectedIds}
                                    onSelectOne={onSelectOne}
                                    defaultOpen={idx === 0}
                                />
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default FDAResultsList;